"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __toCommonJS = (mod) => __copyProps(__defProp({}, "__esModule", { value: true }), mod);

// src/index.ts
var index_exports = {};
__export(index_exports, {
  AuditErrorBoundary: () => AuditErrorBoundary,
  Buffer: () => Buffer2,
  DEFAULT_DEV_ENDPOINT: () => DEFAULT_DEV_ENDPOINT,
  Flusher: () => Flusher,
  _resetForTest: () => _resetForTest,
  flush: () => flush,
  getConfig: () => getConfig,
  init: () => init,
  installGlobalHandlers: () => installGlobalHandlers,
  newReqId: () => newReqId,
  push: () => push,
  resetConfigForTest: () => resetConfigForTest,
  sendBatch: () => sendBatch,
  useAuditClick: () => useAuditClick,
  useAuditPageView: () => useAuditPageView
});
module.exports = __toCommonJS(index_exports);

// src/buffer.ts
var Buffer2 = class {
  constructor(cap) {
    this.cap = cap;
    this.items = [];
  }
  push(ev) {
    this.items.push(ev);
    if (this.items.length > this.cap) {
      this.items = this.items.slice(this.items.length - this.cap);
    }
  }
  size() {
    return this.items.length;
  }
  /** Removes and returns all buffered events. */
  drain() {
    const out = this.items;
    this.items = [];
    return out;
  }
  /** Non-destructive view of current contents. */
  peek() {
    return this.items;
  }
};

// src/config.ts
var _config = null;
function init(opts) {
  _config = {
    app: opts.app,
    endpoint: opts.endpoint,
    enabled: opts.enabled ?? true,
    flushIntervalMs: opts.flushIntervalMs ?? 1e4,
    flushSize: opts.flushSize ?? 20,
    maxBuffer: opts.maxBuffer ?? 200,
    withCredentials: opts.withCredentials ?? true,
    debug: opts.debug ?? false
  };
}
function getConfig() {
  if (!_config) throw new Error("@whalelo/audit-client: init() must be called before using the SDK");
  return _config;
}
function resetConfigForTest() {
  _config = null;
}

// src/flusher.ts
var Flusher = class {
  constructor(buf, sender) {
    this.buf = buf;
    this.sender = sender;
    this.timer = null;
    this.unloadHandler = null;
  }
  start() {
    const cfg = getConfig();
    this.timer = setInterval(() => {
      this.flush().catch(() => {
      });
    }, cfg.flushIntervalMs);
    this.unloadHandler = () => this.beaconFlush();
    window.addEventListener("beforeunload", this.unloadHandler);
    window.addEventListener("pagehide", this.unloadHandler);
  }
  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.unloadHandler) {
      window.removeEventListener("beforeunload", this.unloadHandler);
      window.removeEventListener("pagehide", this.unloadHandler);
      this.unloadHandler = null;
    }
  }
  /** Called after each push — triggers async flush if size threshold reached. */
  notifyAdded() {
    if (this.buf.size() >= getConfig().flushSize) {
      Promise.resolve().then(() => this.flush().catch(() => {
      }));
    }
  }
  async flush() {
    const events = this.buf.drain();
    if (events.length === 0) return;
    await this.sender(events);
  }
  /** Synchronous best-effort flush for beforeunload — uses sendBeacon when available. */
  beaconFlush() {
    const events = this.buf.drain();
    if (events.length === 0) return;
    const cfg = getConfig();
    const body = JSON.stringify({ events });
    if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      navigator.sendBeacon(cfg.endpoint, new Blob([body], { type: "application/json" }));
      return;
    }
    fetch(cfg.endpoint, {
      method: "POST",
      credentials: cfg.withCredentials ? "include" : "same-origin",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true
    }).catch(() => {
    });
  }
};

// src/transport.ts
var RETRY_DELAYS_MS = [1e3, 3e3, 9e3];
async function sendBatch(events) {
  const cfg = getConfig();
  if (!cfg.enabled || events.length === 0) return { ok: true };
  let lastErr;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const res = await fetch(cfg.endpoint, {
        method: "POST",
        credentials: cfg.withCredentials ? "include" : "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events }),
        keepalive: true
        // needed for beforeunload flushes
      });
      if (res.status >= 200 && res.status < 300) {
        return {
          ok: true,
          serverReqId: res.headers.get("X-Audit-Req-Id") ?? void 0
        };
      }
      if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        if (cfg.debug) console.warn("[audit] sendBatch rejected:", res.status);
        return { ok: false };
      }
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }
    if (attempt < RETRY_DELAYS_MS.length) {
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
  if (cfg.debug) console.warn("[audit] sendBatch failed after all retries:", lastErr);
  return { ok: false };
}

// src/reqId.ts
function newReqId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : r & 3 | 8).toString(16);
  });
}

// src/globals.ts
var _handlersInstalled = false;
function installGlobalHandlers(opts) {
  if (typeof window === "undefined") return;
  if (_handlersInstalled) return;
  _handlersInstalled = true;
  const { app, push: push2 } = opts;
  window.addEventListener("error", (ev) => {
    push2({
      app,
      category: "error",
      action: "error.unhandled",
      occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
      summary: ev.message || "Unhandled error",
      severity: 3,
      meta: {
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        stack: ev.error?.stack ?? null
      }
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    push2({
      app,
      category: "error",
      action: "error.unhandled_rejection",
      occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
      summary: reason?.message ?? String(reason) ?? "Unhandled promise rejection",
      severity: 3,
      meta: {
        stack: reason?.stack ?? null
      }
    });
  });
  if (getConfig().debug) console.debug("[audit] global error handlers installed");
}

// src/hooks/useAuditClick.ts
var import_react = require("react");
function useAuditClick(action, meta, push2) {
  return (0, import_react.useCallback)(() => {
    const reqId = newReqId();
    if (push2) {
      push2({
        app: getConfig().app,
        category: "ui_action",
        action,
        occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
        severity: 0,
        meta: meta ?? null,
        req_id: reqId
      });
    }
    return reqId;
  }, [action, meta, push2]);
}

// src/hooks/useAuditPageView.ts
var import_react2 = require("react");
function useAuditPageView(pathname, push2) {
  const prevPathRef = (0, import_react2.useRef)(null);
  (0, import_react2.useEffect)(() => {
    if (!push2) return;
    if (pathname === prevPathRef.current) return;
    prevPathRef.current = pathname;
    push2({
      app: getConfig().app,
      category: "page_view",
      action: "page.view",
      occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
      summary: `Page viewed: ${pathname}`,
      severity: 0,
      meta: { pathname }
    });
  }, [pathname, push2]);
}

// src/boundary/AuditErrorBoundary.tsx
var import_react3 = require("react");
var AuditErrorBoundary = class extends import_react3.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    const { push: push2 } = this.props;
    if (!push2) return;
    const app = this.props.app ?? getConfig().app;
    push2({
      app,
      category: "error",
      action: "error.react_boundary",
      occurred_at: (/* @__PURE__ */ new Date()).toISOString(),
      summary: error.message || "React render error caught by AuditErrorBoundary",
      severity: 3,
      meta: {
        stack: error.stack ?? null,
        componentStack: info.componentStack ?? null
      }
    });
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
};

// src/index.ts
var _buffer = null;
var _flusher = null;
function getBuffer() {
  if (!_buffer) {
    const cfg = getConfig();
    _buffer = new Buffer2(cfg.maxBuffer);
    _flusher = new Flusher(_buffer, sendBatch);
    if (typeof window !== "undefined") {
      _flusher.start();
    }
  }
  return _buffer;
}
function push(event) {
  const cfg = getConfig();
  if (!cfg.enabled) return;
  const buf = getBuffer();
  buf.push(event);
  _flusher?.notifyAdded();
}
async function flush() {
  await _flusher?.flush();
}
function _resetForTest() {
  _flusher?.stop();
  _buffer = null;
  _flusher = null;
}
var DEFAULT_DEV_ENDPOINT = "http://localhost:3100/api/audit/event";
// Annotate the CommonJS export names for ESM import in node:
0 && (module.exports = {
  AuditErrorBoundary,
  Buffer,
  DEFAULT_DEV_ENDPOINT,
  Flusher,
  _resetForTest,
  flush,
  getConfig,
  init,
  installGlobalHandlers,
  newReqId,
  push,
  resetConfigForTest,
  sendBatch,
  useAuditClick,
  useAuditPageView
});
//# sourceMappingURL=index.cjs.map